import { useLeavePrompt } from '../lib/leavePrompt'
import { BackLink } from '../components/kit/BackLink'
import { useCallback, useEffect, useMemo, useRef, useState, type FormEvent } from 'react'

import '../styles/studio.css'
import '../styles/prose.css'

// The typed client in src/lib/api.ts was being rewritten alongside this screen,
// so the two calls this view makes are made here rather than by adding methods
// to a file another pair of hands was already in.
//
// The credential is NOT read locally any more. This screen kept its own reader
// of the development token key, which meant that on any deployment verifying
// real tokens it presented a dev: string the API refuses, and this one screen
// answered 401 while every other screen worked. Importing the shared bearer
// keeps the two in step by construction rather than by a comment asking them
// to be.
import { bearerToken, can, refusalReason } from '../lib/api'
import { Dialog, StageTrack, Status, Toggletip, type Stage, type StageState } from '../components/kit'
import { ErrorReference } from '../components/ui'

// # HOW THIS SCREEN READS
//
// An import is four steps, drawn as a track at the top: the document is
// uploaded and checked, it is mapped to where it lands, what it would create is
// reviewed, and the draft it writes is published later from the questionnaire
// itself. Each panel below is one of those steps. The rules and the reasons for
// them are unchanged; a reason that used to sit in a paragraph under a field is
// behind that field's (i), and a problem's short description is on the row
// with the reason it matters one press away.

// These four mirror packages/core and internal/api exactly. Every rule below is
// enforced by the server whatever this screen does; it is repeated here so an
// author fixing eleven bad keys fixes eleven of them in one pass instead of
// meeting them one refusal at a time.
const KEY_SHAPE = /^[A-Za-z0-9][A-Za-z0-9_.-]{0,62}$/
const CODE_SHAPE = /^[A-Z0-9][A-Z0-9_-]{0,63}$/
const ANSWER_TYPES = ['SINGLE_CHOICE', 'MULTI_CHOICE', 'BOOLEAN', 'SCALE', 'NUMBER', 'DATE', 'TEXT', 'FILE']
const SCORED_TYPES = new Set(['SINGLE_CHOICE', 'MULTI_CHOICE', 'BOOLEAN', 'SCALE'])
const PURPOSES = ['ASSESSMENT', 'INFORMATIONAL', 'PROFILING']

// `why` is this screen's own: the reason a rule exists, shown behind the (i)
// on the problem's row. Issues the API returns carry no reason and show none.
type Issue = { field: string; code: string; detail: string; why?: string }

// reference: the request id the API put on the answer (release 3.1), empty
// when nothing reached the server.
type Failure = { status: number; code: string; message: string; issues: Issue[]; reference?: string }

type Outcome<T> = { ok: true; value: T } | { ok: false; failure: Failure }

type Option = { value: string; label: string; score: number }

type Condition = { questionKey: string; values: string[] }

type Question = {
  key: string
  prompt: string
  helpText: string
  answerType: string
  options: Option[]
  isRequired: boolean
  allowsNa: boolean
  evidenceRequired: boolean
  visibleWhen: Condition | null
  citations: string[]
}

type Section = { key: string; title: string; description: string; questions: Question[] }

type Doc = {
  questionnaireId: string
  code: string
  name: string
  description: string
  purpose: string
  sourcePack: string
  notes: string
  sections: Section[]
}

type Analysis = {
  source: string
  parseError: string | null
  doc: Doc | null
  issues: Issue[]
  warnings: string[]
}

type Listed = {
  id: string
  code: string
  name: string
  purpose: string
  isArchived: boolean
  publishedVersionId?: string
  publishedVersionNo?: number
  versionCount: number
}

// The published version this pack is compared against. A failed fetch has to
// be a state of its own: as a bare null it looked exactly like a questionnaire
// with nothing published, so the screen said nothing about keys and then told
// the author nothing would refuse the document, while the API held a key that
// differed only in case and refused it with KEY_DRIFT.
type DriftCheck =
  | { state: 'none' }
  | { state: 'loading' }
  | { state: 'ready'; versionNo: number; keys: Map<string, string> }
  | { state: 'failed'; failure: Failure }

type ImportResult = {
  questionnaireId: string
  questionnaireCreated: boolean
  versionId: string
  versionNo: number
  status: string
  sections: number
  questionCount: number
  note: string
}

// A toggletip holds 200 characters. A sentence that arrives from the API can
// be longer; it is cut at a word here.
function tip(text: string): string {
  const t = text.trim()
  if (t.length <= 200) return t
  const cut = t.slice(0, 196)
  const space = cut.lastIndexOf(' ')
  return (space > 140 ? cut.slice(0, space) : cut) + '...'
}

function words(code: string): string {
  return code.replace(/_/g, ' ').toLowerCase().replace(/^./, (c) => c.toUpperCase())
}

// ------------------------------------------------------------------ transport

function isObj(v: unknown): v is Record<string, unknown> {
  return typeof v === 'object' && v !== null && !Array.isArray(v)
}

function asText(v: unknown): string {
  return typeof v === 'string' ? v : ''
}

function issuesOf(v: unknown): Issue[] {
  if (!Array.isArray(v)) return []
  const out: Issue[] = []
  for (const raw of v) {
    if (!isObj(raw)) continue
    out.push({ field: asText(raw.field), code: asText(raw.code), detail: asText(raw.detail) })
  }
  return out
}

async function call<T>(path: string, init: RequestInit = {}): Promise<Outcome<T>> {
  const headers = new Headers(init.headers)
  headers.set('Authorization', 'Bearer ' + (await bearerToken()))
  headers.set('Accept', 'application/json')
  if (init.body) headers.set('Content-Type', 'application/json')

  let res: Response
  try {
    res = await fetch(path, { ...init, headers })
  } catch {
    return {
      ok: false,
      failure: {
        status: 0,
        code: 'NETWORK',
        message: 'Could not reach ' + path + '. Confirm tprm-api is listening, then try again.',
        issues: [],
      },
    }
  }

  const text = await res.text()
  let body: unknown = null
  if (text.trim() !== '') {
    try {
      body = JSON.parse(text)
    } catch {
      return {
        ok: false,
        failure: {
          status: res.status,
          code: 'BAD_RESPONSE',
          message: path + ' answered ' + res.status + ' with a body that is not JSON.',
          issues: [],
        },
      }
    }
  }

  if (!res.ok) {
    const b = isObj(body) ? body : {}
    return {
      ok: false,
      failure: {
        status: res.status,
        code: asText(b.code) || asText(b.error) || 'HTTP_' + res.status,
        message: asText(b.message) || path + ' returned ' + res.status + '.',
        issues: issuesOf(b.issues),
        reference: asText(b.requestId) || res.headers.get('X-Request-ID') || '',
      },
    }
  }

  // An empty body on a 2xx is what the dev proxy returns when the API is not
  // listening. Reading it as a result is how a screen reports a confident
  // success for a call that never happened.
  if (body === null) {
    return {
      ok: false,
      failure: {
        status: res.status,
        code: 'EMPTY_RESPONSE',
        message: path + ' answered ' + res.status + ' with an empty body.',
        issues: [],
      },
    }
  }
  return { ok: true, value: body as T }
}

function remedy(f: Failure): string {
  switch (f.status) {
    case 0:
      return 'Nothing was written and the document is untouched. Retry in a moment.'
    case 401:
      return 'Your sign-in was not accepted. Sign in again and retry.'
    case 403:
      return refusalReason('questionnaire.author')
    case 404:
      return 'No such questionnaire here. Pick one from the list rather than typing an id.'
    case 409:
      return 'That identifier is taken. Add a version to that questionnaire instead.'
    case 422:
      return 'The document was read and refused. Each issue below names the field.'
    default:
      return 'Retry once. If it fails again, give support the time and what you were doing.'
  }
}

// ------------------------------------------------------------------ reading

const KEY_RULE = 'letters, digits, hyphen, underscore or dot, no spaces, up to 63'

function wrongType(field: string, expected: string): Issue {
  return {
    field,
    code: 'WRONG_TYPE',
    detail: 'Expected ' + expected,
    why: 'The document is read in one pass, so a field of the wrong JSON type is refused with 400 before any other problem is reported. Fix this one first.',
  }
}

function pickText(o: Record<string, unknown>, prop: string, field: string, issues: Issue[]): string {
  const v = o[prop]
  if (v === undefined || v === null) return ''
  if (typeof v !== 'string') {
    issues.push(wrongType(field, 'a string'))
    return ''
  }
  return v
}

function pickFlag(o: Record<string, unknown>, prop: string, field: string, issues: Issue[], fallback: boolean): boolean {
  const v = o[prop]
  if (v === undefined || v === null) return fallback
  if (typeof v !== 'boolean') {
    issues.push(wrongType(field, 'true or false'))
    return fallback
  }
  return v
}

function pickList(o: Record<string, unknown>, prop: string, field: string, issues: Issue[]): unknown[] {
  const v = o[prop]
  if (v === undefined || v === null) return []
  if (!Array.isArray(v)) {
    issues.push(wrongType(field, 'an array'))
    return []
  }
  return v
}

function pickStrings(o: Record<string, unknown>, prop: string, field: string, issues: Issue[]): string[] {
  const out: string[] = []
  const list = pickList(o, prop, field, issues)
  for (let i = 0; i < list.length; i++) {
    const v = list[i]
    if (typeof v !== 'string') {
      issues.push(wrongType(field + '[' + i + ']', 'a string'))
      continue
    }
    out.push(v)
  }
  return out
}

function readCondition(raw: unknown, field: string, issues: Issue[]): Condition | null {
  // Omission and an explicit null mean the same thing, which is what the API
  // does with them too: a form that reads a question back and sends it again
  // writes "visibleWhen": null on every unconditional question.
  if (raw === undefined || raw === null) return null
  if (!isObj(raw)) {
    issues.push({ field, code: 'INVALID', detail: 'The one conditional form is {"question_key":"...","in":["..."]}' })
    return null
  }
  const key = asText(raw.question_key)
  const values: string[] = []
  if (raw.in !== undefined && raw.in !== null) {
    if (!Array.isArray(raw.in)) {
      issues.push({ field, code: 'INVALID', detail: 'The one conditional form is {"question_key":"...","in":["..."]}' })
      return null
    }
    for (const v of raw.in) {
      if (typeof v !== 'string') {
        issues.push({ field, code: 'INVALID', detail: 'Every value in the in list has to be the string a vendor would answer.' })
        return null
      }
      values.push(v)
    }
  }
  if (key.trim() === '') {
    issues.push({ field, code: 'INVALID', detail: 'The one conditional form is {"question_key":"...","in":["..."]}' })
    return null
  }
  return { questionKey: key, values }
}

function analyse(source: string): Analysis {
  const empty: Analysis = { source, parseError: null, doc: null, issues: [], warnings: [] }
  if (source.trim() === '') {
    return { ...empty, parseError: 'Paste a document or choose a file first.' }
  }

  let parsed: unknown
  try {
    parsed = JSON.parse(source)
  } catch (err) {
    return { ...empty, parseError: err instanceof Error ? err.message : 'The document is not valid JSON.' }
  }
  if (!isObj(parsed)) {
    return { ...empty, parseError: 'The document has to be a JSON object carrying a sections array, not ' + (Array.isArray(parsed) ? 'an array' : typeof parsed) + '.' }
  }

  const issues: Issue[] = []
  const warnings: string[] = []

  const doc: Doc = {
    questionnaireId: pickText(parsed, 'questionnaireId', 'questionnaireId', issues).trim(),
    code: pickText(parsed, 'code', 'code', issues).trim(),
    name: pickText(parsed, 'name', 'name', issues).trim(),
    description: pickText(parsed, 'description', 'description', issues).trim(),
    purpose: pickText(parsed, 'purpose', 'purpose', issues).trim().toUpperCase(),
    sourcePack: pickText(parsed, 'sourcePack', 'sourcePack', issues).trim(),
    notes: pickText(parsed, 'notes', 'notes', issues).trim(),
    sections: [],
  }

  // A sections field of the wrong type is already reported as WRONG_TYPE, and
  // saying it is also missing sends the author looking for a second fault.
  const malformed = parsed.sections !== undefined && parsed.sections !== null && !Array.isArray(parsed.sections)
  const rawSections = pickList(parsed, 'sections', 'sections', issues)
  if (rawSections.length === 0 && !malformed) {
    issues.push({
      field: 'sections',
      code: 'REQUIRED',
      detail: 'Questions have to arrive in sections',
      why: 'A flat list is unusable on the vendor edge: it cannot be paginated, saved part way or reissued as a delta, and section keys are what a trigger reopens.',
    })
  }

  const seenSections = new Map<string, number>()
  const seenQuestions = new Map<string, string>()

  for (let si = 0; si < rawSections.length; si++) {
    const where = 'sections[' + si + ']'
    const raw = rawSections[si]
    if (!isObj(raw)) {
      issues.push(wrongType(where, 'an object with key, title and questions'))
      continue
    }

    const key = pickText(raw, 'key', where + '.key', issues).trim()
    const title = pickText(raw, 'title', where + '.title', issues).trim()
    if (key === '') {
      issues.push({ field: where + '.key', code: 'REQUIRED', detail: 'Every section needs a key.' })
    } else if (!KEY_SHAPE.test(key)) {
      issues.push({ field: where + '.key', code: 'INVALID', detail: 'Section key ' + key + ': ' + KEY_RULE })
    } else {
      const prev = seenSections.get(key.toLowerCase())
      if (prev !== undefined) {
        issues.push({ field: where + '.key', code: 'DUPLICATE', detail: 'Section key ' + key + ' is already used by sections[' + prev + '].' })
      }
      seenSections.set(key.toLowerCase(), si)
    }
    if (title === '') {
      issues.push({ field: where + '.title', code: 'REQUIRED', detail: 'The title is the heading the vendor reads above the questions.' })
    }

    const rawQuestions = pickList(raw, 'questions', where + '.questions', issues)
    if (rawQuestions.length === 0) {
      issues.push({ field: where + '.questions', code: 'EMPTY', detail: 'Section ' + (key || '#' + si) + ' carries no questions.' })
    }

    const section: Section = {
      key,
      title,
      description: pickText(raw, 'description', where + '.description', issues).trim(),
      questions: [],
    }

    for (let qi = 0; qi < rawQuestions.length; qi++) {
      const at = where + '.questions[' + qi + ']'
      const rawQ = rawQuestions[qi]
      if (!isObj(rawQ)) {
        issues.push(wrongType(at, 'an object with key, prompt and answerType'))
        continue
      }

      const qKey = pickText(rawQ, 'key', at + '.key', issues).trim()
      if (qKey === '') {
        issues.push({
          field: at + '.key',
          code: 'REQUIRED',
          detail: 'Every question needs a key',
          why: 'The question key is what joins this answer to the same question in the next version and in a spreadsheet round trip.',
        })
      } else if (!KEY_SHAPE.test(qKey)) {
        issues.push({ field: at + '.key', code: 'INVALID', detail: 'Question key ' + qKey + ': ' + KEY_RULE })
      } else {
        const prev = seenQuestions.get(qKey.toLowerCase())
        if (prev !== undefined) {
          issues.push({
            field: at + '.key',
            code: 'DUPLICATE',
            detail: 'Question key ' + qKey + ' is already used by ' + prev,
            why: 'Keys are compared ignoring case, because a spreadsheet round trip does not keep case, so AC-01 and ac-01 are the same question.',
          })
        }
      }
      if (qKey !== '') seenQuestions.set(qKey.toLowerCase(), at)

      const prompt = pickText(rawQ, 'prompt', at + '.prompt', issues).trim()
      if (prompt === '') {
        issues.push({ field: at + '.prompt', code: 'REQUIRED', detail: 'The question the vendor reads.' })
      }

      const given = pickText(rawQ, 'answerType', at + '.answerType', issues).trim().toUpperCase()
      const answerType = given === '' ? 'SINGLE_CHOICE' : given
      if (!ANSWER_TYPES.includes(answerType)) {
        issues.push({
          field: at + '.answerType',
          code: 'INVALID',
          detail: 'Not an answer type this product knows',
          why: 'Answer type is one of ' + ANSWER_TYPES.join(', ') + '.',
        })
      }

      const options: Option[] = []
      const rawOptions = pickList(rawQ, 'options', at + '.options', issues)
      const seenOption = new Set<string>()
      for (let oi = 0; oi < rawOptions.length; oi++) {
        const oAt = at + '.options[' + oi + ']'
        const rawO = rawOptions[oi]
        if (!isObj(rawO)) {
          issues.push(wrongType(oAt, 'an object with value, label and score'))
          continue
        }
        const value = pickText(rawO, 'value', oAt + '.value', issues)
        const label = pickText(rawO, 'label', oAt + '.label', issues)
        let score = 0
        const rawScore = rawO.score
        if (rawScore !== undefined && rawScore !== null) {
          if (typeof rawScore !== 'number' || Number.isNaN(rawScore)) {
            issues.push(wrongType(oAt + '.score', 'a number between 0 and 1'))
          } else {
            score = rawScore
          }
        }
        if (value.trim() === '') {
          issues.push({ field: oAt + '.value', code: 'REQUIRED', detail: 'The stored answer value.' })
        } else if (seenOption.has(value)) {
          issues.push({ field: oAt + '.value', code: 'DUPLICATE', detail: value + ' appears twice.' })
        }
        seenOption.add(value)
        if (label.trim() === '') {
          issues.push({ field: oAt + '.label', code: 'REQUIRED', detail: 'What the vendor sees.' })
        }
        if (score < 0 || score > 1) {
          issues.push({
            field: oAt + '.score',
            code: 'OUT_OF_RANGE',
            detail: 'An option score is a multiplier from 0 to 1',
            why: 'The weight belongs to the risk model, not to the questionnaire.',
          })
        }
        options.push({ value, label, score })
      }

      const scored = SCORED_TYPES.has(answerType)
      if (scored && answerType !== 'BOOLEAN' && options.length < 2) {
        issues.push({
          field: at + '.options',
          code: 'REQUIRED',
          detail: 'A ' + words(answerType).toLowerCase() + ' question needs at least two options',
          why: 'The option score is what the risk model multiplies the question weight by.',
        })
      } else if (!scored && options.length > 0) {
        issues.push({
          field: at + '.options',
          code: 'UNSCORED',
          detail: 'A ' + words(answerType).toLowerCase() + ' answer is not picked from a list',
          why: 'Its options would never be read.',
        })
      }

      const question: Question = {
        key: qKey,
        prompt,
        helpText: pickText(rawQ, 'helpText', at + '.helpText', issues).trim(),
        answerType,
        options,
        isRequired: pickFlag(rawQ, 'isRequired', at + '.isRequired', issues, true),
        allowsNa: pickFlag(rawQ, 'allowsNa', at + '.allowsNa', issues, false),
        evidenceRequired: pickFlag(rawQ, 'evidenceRequired', at + '.evidenceRequired', issues, false),
        visibleWhen: readCondition(rawQ.visibleWhen, 'question ' + (qKey || at) + '.visibleWhen', issues),
        citations: pickStrings(rawQ, 'citations', at + '.citations', issues),
      }
      section.questions.push(question)

      if (scored && options.length > 0 && options.every((o) => o.score === 0)) {
        warnings.push(
          question.key + ' scores 0 on every option, so a risk model weighted on it earns nothing whatever the vendor answers.',
        )
      }
    }

    doc.sections.push(section)
  }

  // Conditions are checked once every key is known: a condition on a key this
  // version does not carry hides the question from every vendor for ever, and
  // the vendor edge treats an unresolvable condition as unanswered rather than
  // erroring, so nothing downstream would ever report it.
  for (const sec of doc.sections) {
    for (const q of sec.questions) {
      const cond = q.visibleWhen
      if (!cond) continue
      const field = 'question ' + (q.key || sec.key) + '.visibleWhen'
      const ref = cond.questionKey.trim().toLowerCase()
      if (ref === q.key) {
        issues.push({ field, code: 'SELF_REFERENCE', detail: 'A question cannot condition on its own answer.' })
        continue
      }
      if (!seenQuestions.has(ref)) {
        issues.push({
          field,
          code: 'UNKNOWN_QUESTION',
          detail: 'It conditions on ' + cond.questionKey + ', which is not in this version',
          why: 'The question would be hidden from every vendor, and nothing downstream would ever report it.',
        })
      } else if (ref === q.key.toLowerCase()) {
        // The API compares the lower-cased reference against the key as
        // written, so a condition naming an upper-case key gets past the
        // self-reference check and resolves to the question itself. It is not
        // refused, and a question that reveals itself never appears.
        warnings.push(
          q.key + ' conditions on ' + cond.questionKey +
            ', which is itself once case is ignored. The API accepts that, and the question never becomes visible.',
        )
      }
      if (cond.values.length === 0) {
        issues.push({ field, code: 'EMPTY_MATCH', detail: 'in is empty, so no answer would ever reveal the question.' })
      }
    }
  }

  return { source, parseError: null, doc, issues, warnings }
}

// ------------------------------------------------------------------ display

function IssueList({ issues }: { issues: Issue[] }) {
  return (
    <ul className="prose-issues">
      {issues.map((i, n) => (
        <li key={i.field + i.code + n}>
          <span className="prose-mono">{i.field}</span>
          <Status tone="crit">{words(i.code)}</Status>
          <span className="prose-line">
            {i.detail}
            {i.why && <Toggletip label={'Why ' + words(i.code).toLowerCase()}>{tip(i.why)}</Toggletip>}
          </span>
        </li>
      ))}
    </ul>
  )
}

export function QuestionnaireImport() {
  const [text, setText] = useState('')
  const [fileName, setFileName] = useState('')
  const [analysis, setAnalysis] = useState<Analysis | null>(null)

  const [mode, setMode] = useState<'existing' | 'new'>('new')
  const [existingId, setExistingId] = useState('')
  const [code, setCode] = useState('')
  const [name, setName] = useState('')
  const [description, setDescription] = useState('')
  const [purpose, setPurpose] = useState('ASSESSMENT')
  const [sourcePack, setSourcePack] = useState('')
  const [notes, setNotes] = useState('')

  const [listed, setListed] = useState<Listed[] | null>(null)
  const [listError, setListError] = useState<Failure | null>(null)
  const [drift, setDrift] = useState<DriftCheck>({ state: 'none' })

  const [busy, setBusy] = useState(false)
  const [failure, setFailure] = useState<Failure | null>(null)
  const [result, setResult] = useState<ImportResult | null>(null)

  const [questionsOpen, setQuestionsOpen] = useState(false)
  const [warningsOpen, setWarningsOpen] = useState(false)

  // A pasted pack or a typed note not yet imported is the one thing here that
  // closing or reloading the tab would lose (Back model B8); once imported it
  // is saved. Moving inside the console does not raise the browser prompt.
  // Typed or pasted text only: a pack read from a file is still on disk, and
  // notes count only while the pack they belong to is on screen.
  useLeavePrompt(result === null && ((text.trim() !== '' && fileName === '') || (analysis?.doc != null && notes.trim() !== '')))

  const fileInput = useRef<HTMLInputElement>(null)
  const verdict = useRef<HTMLHeadingElement>(null)
  const announce = useRef(false)

  useEffect(() => {
    let live = true
    call<{ questionnaires: Listed[] }>('/v1/questionnaires').then((r) => {
      if (!live) return
      if (r.ok) {
        setListed(r.value.questionnaires ?? [])
        setListError(null)
      } else {
        setListed([])
        setListError(r.failure)
      }
    })
    return () => {
      live = false
    }
  }, [])

  const doc = analysis?.doc ?? null

  // The questionnaire this import would actually land in. A code that already
  // exists is not a new questionnaire: the API resolves it and adds a version,
  // ignoring the name and purpose on this form, which is worth saying before
  // somebody types a name nobody will ever see.
  const target = useMemo<Listed | null>(() => {
    if (!listed) return null
    if (mode === 'existing') return listed.find((q) => q.id === existingId) ?? null
    const wanted = code.trim().toUpperCase()
    if (wanted === '') return null
    return listed.find((q) => q.code === wanted) ?? null
  }, [listed, mode, existingId, code])

  useEffect(() => {
    const versionId = target?.publishedVersionId
    if (!versionId) {
      setDrift({ state: 'none' })
      return
    }
    let live = true
    setDrift({ state: 'loading' })
    call<{ sections?: { questions?: { questionKey?: string }[] }[] }>(
      '/v1/questionnaire-versions/' + encodeURIComponent(versionId),
    ).then((r) => {
      if (!live) return
      if (!r.ok) {
        setDrift({ state: 'failed', failure: r.failure })
        return
      }
      const keys = new Map<string, string>()
      for (const s of r.value.sections ?? []) {
        for (const q of s.questions ?? []) {
          const k = q.questionKey
          if (typeof k === 'string' && k !== '') keys.set(k.toLowerCase(), k)
        }
      }
      setDrift({ state: 'ready', versionNo: target?.publishedVersionNo ?? 0, keys })
    })
    return () => {
      live = false
    }
  }, [target?.publishedVersionId, target?.publishedVersionNo])

  const destinationIssues = useMemo<Issue[]>(() => {
    const out: Issue[] = []
    if (!doc) return out
    if (mode === 'existing') {
      if (existingId === '') {
        out.push({
          field: 'questionnaireId',
          code: 'REQUIRED',
          detail: 'Choose the questionnaire this pack becomes the next version of.',
        })
      }
    } else if (!target) {
      const wanted = code.trim().toUpperCase()
      if (wanted === '') {
        out.push({
          field: 'code',
          code: 'REQUIRED',
          detail: 'Give a code and name, or choose an existing questionnaire',
        })
      } else if (!CODE_SHAPE.test(wanted)) {
        out.push({ field: 'code', code: 'INVALID', detail: 'Use upper case letters, digits, hyphen or underscore.' })
      }
      if (name.trim() === '') {
        out.push({ field: 'name', code: 'REQUIRED', detail: 'Name it as the person choosing it would recognise it.' })
      }
      if (!PURPOSES.includes(purpose)) {
        out.push({
          field: 'purpose',
          code: 'INVALID',
          detail: 'Purpose is ASSESSMENT, INFORMATIONAL or PROFILING',
          why: 'INFORMATIONAL and PROFILING collect answers without scoring them.',
        })
      }
    }

    // Key drift. This is the one refusal an author cannot see coming from the
    // file alone: a key that came back from a spreadsheet differing only in
    // case from the published one is a second question, and every answer given
    // to the first stops following it.
    if (drift.state === 'ready') {
      for (const sec of doc.sections) {
        for (const q of sec.questions) {
          const prior = drift.keys.get(q.key.toLowerCase())
          if (!prior || prior === q.key) continue
          out.push({
            field: 'question ' + q.key,
            code: 'KEY_DRIFT',
            detail: 'Published as ' + prior + ', which differs only in case. Use ' + prior,
            why:
              'Both spellings would be two questions: every answer given to ' + prior +
              ' stops following it, and a risk model weighted on ' + prior + ' earns nothing from it.',
          })
        }
      }
    }
    return out
  }, [doc, mode, existingId, target, code, name, purpose, drift])

  const stats = useMemo(() => {
    if (!doc) return null
    const mix = new Map<string, number>()
    let questions = 0
    let scored = 0
    let optionCount = 0
    let citations = 0
    let conditional = 0
    let evidence = 0
    let optional = 0
    let allowsNa = 0
    for (const sec of doc.sections) {
      for (const q of sec.questions) {
        questions++
        mix.set(q.answerType, (mix.get(q.answerType) ?? 0) + 1)
        if (SCORED_TYPES.has(q.answerType)) scored++
        optionCount += q.options.length
        if (q.citations.length > 0) citations++
        if (q.visibleWhen) conditional++
        if (q.evidenceRequired) evidence++
        if (!q.isRequired) optional++
        if (q.allowsNa) allowsNa++
      }
    }
    const rows = [...mix.entries()].sort((a, b) => b[1] - a[1] || a[0].localeCompare(b[0]))
    return { questions, scored, optionCount, citations, conditional, evidence, optional, allowsNa, mix: rows }
  }, [doc])

  const stale = analysis !== null && analysis.source !== text
  const blocking = analysis === null ? [] : doc === null ? analysis.issues : [...analysis.issues, ...destinationIssues]
  // A second press on a document that already landed writes a second draft
  // version of the same content, which is not a mistake anything downstream
  // reports. Re-checking the document is what re-arms the button.
  //
  // Held while the published version is still being read, because until it
  // arrives the key comparison has not run and the button would be offering a
  // document the API is about to refuse with KEY_DRIFT.
  const ready =
    doc !== null && !stale && blocking.length === 0 && result === null && drift.state !== 'loading'

  useEffect(() => {
    if (!announce.current) return
    announce.current = false
    verdict.current?.focus()
  }, [analysis])

  const check = useCallback(
    (source: string) => {
      const next = analyse(source)
      setAnalysis(next)
      setFailure(null)
      setResult(null)
      announce.current = true
      if (!next.doc) return

      // The destination is re-read from the document on every check, so the
      // form always says what the file says rather than what a previous file
      // said.
      setSourcePack(next.doc.sourcePack)
      setNotes(next.doc.notes)
      const known = listed?.find((q) => q.id === next.doc?.questionnaireId)
      if (next.doc.questionnaireId !== '' && known) {
        setMode('existing')
        setExistingId(known.id)
      } else {
        setMode('new')
        setCode(next.doc.code)
        setName(next.doc.name)
        setDescription(next.doc.description)
        setPurpose(next.doc.purpose === '' ? 'ASSESSMENT' : next.doc.purpose)
      }
    },
    [listed],
  )

  async function chooseFile(file: File | null) {
    if (!file) return
    setFileName(file.name)
    const content = await file.text()
    setText(content)
    check(content)
  }

  function onCheck(e: FormEvent) {
    e.preventDefault()
    check(text)
  }

  async function runImport() {
    if (!doc || !ready) return
    setBusy(true)
    setFailure(null)
    setResult(null)

    const body: Record<string, unknown> = {
      sourcePack,
      notes,
      sections: doc.sections.map((s) => ({
        key: s.key,
        title: s.title,
        description: s.description,
        questions: s.questions.map((q) => ({
          key: q.key,
          prompt: q.prompt,
          helpText: q.helpText,
          answerType: q.answerType,
          options: q.options,
          isRequired: q.isRequired,
          allowsNa: q.allowsNa,
          evidenceRequired: q.evidenceRequired,
          visibleWhen: q.visibleWhen ? { question_key: q.visibleWhen.questionKey, in: q.visibleWhen.values } : null,
          citations: q.citations,
        })),
      })),
    }
    if (mode === 'existing') {
      body.questionnaireId = existingId
    } else {
      body.code = code.trim().toUpperCase()
      body.name = name.trim()
      body.description = description.trim()
      body.purpose = purpose
    }

    const r = await call<ImportResult>('/v1/questionnaires/import', { method: 'POST', body: JSON.stringify(body) })
    setBusy(false)
    if (r.ok) {
      setResult(r.value)
      const fresh = await call<{ questionnaires: Listed[] }>('/v1/questionnaires')
      if (fresh.ok) setListed(fresh.value.questionnaires ?? [])
    } else {
      setFailure(r.failure)
    }
  }

  const effectivePurpose = target ? target.purpose : purpose
  const mayAuthor = can('questionnaire.author')

  // Where the import stands, from the same state the panels below are drawn
  // from. Publishing is never done here: the import writes a draft, and the
  // track says the last step happens on the questionnaire.
  const uploaded = doc !== null && !stale
  const docIssues = analysis?.issues.length ?? 0
  const mapped = uploaded && destinationIssues.length === 0 && drift.state !== 'loading'
  const uploadState: StageState = uploaded ? 'done' : analysis?.parseError != null || (analysis !== null && doc === null) ? 'blocked' : 'current'
  const mapState: StageState = !uploaded ? 'upcoming' : mapped ? 'done' : 'current'
  const reviewState: StageState = !mapped ? 'upcoming' : result !== null ? 'done' : docIssues > 0 ? 'blocked' : 'current'
  const publishState: StageState = result !== null ? 'current' : 'upcoming'
  const stages: Stage[] = [
    { key: 'upload', label: 'Upload', state: uploadState, sub: fileName || (uploaded ? 'pasted' : undefined) },
    {
      key: 'map',
      label: 'Map',
      state: mapState,
      sub: uploaded ? (target ? target.code : code.trim().toUpperCase() || undefined) : undefined,
    },
    {
      key: 'review',
      label: 'Review',
      state: reviewState,
      sub: stats ? stats.questions + ' questions' : undefined,
      meta: docIssues > 0 && uploaded ? docIssues + ' problems' : undefined,
    },
    { key: 'publish', label: 'Publish', state: publishState, sub: result ? 'on the questionnaire' : 'after a draft' },
  ]

  return (
    <div className="prose-page">
      <div className="prose-head">
        <div className="prose-head-main">
          <BackLink fallback={{ label: 'Questionnaires', href: '#/questionnaires' }} />
          <h1>Import a questionnaire</h1>
          <div className="prose-meta">
            <span>A JSON pack becomes a draft version</span>
            <Toggletip label="About importing">
              Nothing is published from this screen. The draft is published from the questionnaire, where the risk
              model check runs.
            </Toggletip>
          </div>
        </div>
      </div>

      <section className="panel prose-import-track" aria-label="Where this import is">
        <StageTrack stages={stages} label="Import progress" />
      </section>

      <section className="panel" aria-labelledby="doc-heading">
        <div className="prose-panelhead">
          <h2 id="doc-heading">1. The document</h2>
          {stale && (
            <>
              <Status tone="warn">Changed</Status>
              <span className="prose-sub">Check it again</span>
            </>
          )}
          {uploaded && <Status tone="ok">Checked</Status>}
        </div>
        <form onSubmit={onCheck} className="prose-drawer-stack">
          <div className="prose-drop">
            <div className="field">
              <span className="prose-labelrow">
                <label htmlFor="import-json-file">JSON file</label>
                <Toggletip label="About the file">
                  The file is read into the box below, where it can be corrected before anything is sent.
                </Toggletip>
              </span>
              <input
                id="import-json-file"
                ref={fileInput}
                type="file"
                accept=".json,application/json"
                onChange={(e) => void chooseFile(e.target.files?.[0] ?? null)}
              />
            </div>
            {fileName !== '' && (
              <span className="prose-sub">
                Read from <strong>{fileName}</strong>
              </span>
            )}
          </div>

          <div className="field">
            <span className="prose-labelrow">
              <label htmlFor="import-json">Or paste the document</label>
              <Toggletip label="What the document holds">
                A JSON object with questionnaireId or code, name, description and purpose, plus sourcePack, notes and
                sections. Each section is key, title, description and questions.
              </Toggletip>
            </span>
            <textarea
              id="import-json"
              className="import-doc prose-doc"
              spellCheck={false}
              value={text}
              onChange={(e) => setText(e.target.value)}
              placeholder='{"code":"CAIQ-V4","name":"CAIQ v4","purpose":"ASSESSMENT","sections":[...]}'
            />
          </div>

          <div className="prose-buttons">
            <button type="submit" className={doc === null || stale ? 'primary' : undefined}>
              Check the document
            </button>
            <button
              type="button"
              onClick={() => {
                setText('')
                setFileName('')
                setAnalysis(null)
                setFailure(null)
                setResult(null)
                if (fileInput.current) fileInput.current.value = ''
              }}
            >
              Clear
            </button>
          </div>
        </form>
      </section>

      {analysis?.parseError != null && (
        <div className="error-box" role="alert">
          <h3 ref={verdict} tabIndex={-1}>
            This is not a document the API could read
          </h3>
          <p>{analysis.parseError}</p>
        </div>
      )}

      {blocking.length > 0 && (
        <section className="panel" role="alert" aria-labelledby="problems-heading">
          <div className="prose-panelhead">
            <h2 id="problems-heading" ref={analysis?.parseError == null ? verdict : null} tabIndex={-1}>
              Problems to fix
            </h2>
            <Status tone="crit">{blocking.length} blocking</Status>
            <Toggletip label="How problems are reported">
              {blocking.some((i) => i.code === 'WRONG_TYPE')
                ? 'Every one is listed. The API refuses a wrong type with 400 before reading the rest, so fix those first; the others come back as 422 on these fields.'
                : 'Every one is listed, not just the first. The API applies the same rules and would refuse the document with 422 on these fields.'}
            </Toggletip>
          </div>
          <IssueList issues={blocking} />
        </section>
      )}

      {doc && (
        <section className="panel" aria-labelledby="dest-heading">
          <div className="prose-panelhead">
            <h2 id="dest-heading">2. Where it lands</h2>
            {mapped && <Status tone="ok">Mapped</Status>}
          </div>

          <fieldset className="fieldset">
            <legend className="vh">Destination</legend>
            <div className="prose-dest">
              <label className="prose-choice" htmlFor="dest-existing">
                <input
                  type="radio"
                  id="dest-existing"
                  name="dest"
                  checked={mode === 'existing'}
                  onChange={() => setMode('existing')}
                />
                <span className="prose-row-main">
                  A new version of an existing one
                  <span className="prose-sub">Question keys carry the answers forward</span>
                </span>
              </label>
              <label className="prose-choice" htmlFor="dest-new">
                <input type="radio" id="dest-new" name="dest" checked={mode === 'new'} onChange={() => setMode('new')} />
                <span className="prose-row-main">
                  A new questionnaire
                  <span className="prose-sub">Created with version 1 in draft, in one call</span>
                </span>
              </label>
            </div>

            {mode === 'existing' ? (
              <div className="field" style={{ marginTop: 12 }}>
                <span className="prose-labelrow">
                  <label htmlFor="dest-questionnaire">Questionnaire</label>
                  <Toggletip label="Why purpose is not asked">
                    The purpose belongs to the questionnaire, not to this pack, so it is not asked for again here.
                  </Toggletip>
                </span>
                <select id="dest-questionnaire" value={existingId} onChange={(e) => setExistingId(e.target.value)}>
                  <option value="">Choose one</option>
                  {(listed ?? [])
                    .filter((q) => !q.isArchived)
                    .map((q) => (
                      <option key={q.id} value={q.id}>
                        {q.code} - {q.name} ({q.purpose.toLowerCase()}, {q.versionCount}{' '}
                        {q.versionCount === 1 ? 'version' : 'versions'})
                      </option>
                    ))}
                </select>
              </div>
            ) : (
              <div className="form-grid" style={{ marginTop: 12 }}>
                <div className="field">
                  <span className="prose-labelrow">
                    <label htmlFor="dest-code">Code</label>
                    <Toggletip label="About the code">
                      Upper case letters, digits, hyphen or underscore. A campaign names a questionnaire by its code.
                    </Toggletip>
                  </span>
                  <input id="dest-code" type="text" value={code} onChange={(e) => setCode(e.target.value)} />
                </div>
                <div className="field">
                  <label htmlFor="dest-name">Name</label>
                  <input id="dest-name" type="text" value={name} onChange={(e) => setName(e.target.value)} />
                </div>
                <div className="field">
                  <span className="prose-labelrow">
                    <label htmlFor="dest-purpose">Purpose</label>
                    <Toggletip label="About purpose">
                      Informational and profiling collect without scoring. An assessment is checked against its risk
                      model at publish.
                    </Toggletip>
                  </span>
                  <select id="dest-purpose" value={purpose} onChange={(e) => setPurpose(e.target.value)}>
                    {(PURPOSES.includes(purpose) ? PURPOSES : [purpose, ...PURPOSES]).map((p) => (
                      <option key={p} value={p}>
                        {p}
                      </option>
                    ))}
                  </select>
                </div>
                <div className="field field-wide">
                  <label htmlFor="dest-description">Description</label>
                  <textarea
                    id="dest-description"
                    rows={2}
                    value={description}
                    onChange={(e) => setDescription(e.target.value)}
                  />
                </div>
              </div>
            )}
          </fieldset>

          <div className="form-grid">
            <div className="field">
              <span className="prose-labelrow">
                <label htmlFor="dest-pack">Source pack</label>
                <Toggletip label="About the source pack">
                  Where the content came from, such as CAIQ v4 or SIG Lite 2025, so a reviewer later knows whose
                  questions these are.
                </Toggletip>
              </span>
              <input id="dest-pack" type="text" value={sourcePack} onChange={(e) => setSourcePack(e.target.value)} />
            </div>
            <div className="field field-wide">
              <label htmlFor="dest-notes">Notes</label>
              <textarea id="dest-notes" rows={2} value={notes} onChange={(e) => setNotes(e.target.value)} />
            </div>
          </div>

          <div className="prose-drawer-stack" style={{ marginTop: 4 }}>
            {target && mode === 'new' && (
              <p className="prose-line">
                <Status tone="warn">Code exists</Status>
                Becomes version {target.versionCount + 1} of {target.name}
                <Toggletip label="What happens to the typed name">
                  {tip(
                    'The name, description and purpose typed above are ignored, and the questionnaire keeps the purpose it has, ' +
                      target.purpose +
                      '.',
                  )}
                </Toggletip>
              </p>
            )}
            {target && drift.state === 'ready' && (
              <p className="prose-line">
                <Status tone="ok">Keys checked</Status>
                Against version {drift.versionNo}, {drift.keys.size} questions
              </p>
            )}
            {target && drift.state === 'loading' && (
              <p className="prose-line">
                <Status tone="neutral">Checking keys</Status>
                Reading the published version
              </p>
            )}
            {target && drift.state === 'failed' && (
              <p className="prose-line">
                <Status tone="warn">Keys not checked</Status>
                The published version could not be read ({drift.failure.code})
                <Toggletip label="What that means">
                  {tip(
                    'A key differing from a published one only in case is refused with KEY_DRIFT, so that refusal would arrive on import rather than now. ' +
                      remedy(drift.failure),
                  )}
                </Toggletip>
              </p>
            )}
            {target && !target.publishedVersionId && (
              <p className="prose-line">
                <Status tone="plain">Nothing published</Status>
                No established keys for this pack to drift from
              </p>
            )}
          </div>
        </section>
      )}

      {doc && stats && (
        <section className="panel" aria-labelledby="preview-heading">
          <div className="prose-panelhead">
            <h2 id="preview-heading">3. What this would create</h2>
            <span className="prose-sub">
              One draft version, {stats.optionCount} answer {stats.optionCount === 1 ? 'option' : 'options'}
            </span>
            <span className="prose-grow" />
            <button type="button" className="prose-link" onClick={() => setQuestionsOpen(true)}>
              Every question
            </button>
          </div>

          <dl className="prose-metrics" style={{ gridTemplateColumns: 'repeat(auto-fit, minmax(110px, 1fr))' }}>
            <div>
              <dt>Sections</dt>
              <dd>{doc.sections.length}</dd>
            </div>
            <div>
              <dt>Questions</dt>
              <dd>{stats.questions}</dd>
            </div>
            <div>
              <dt>Scored</dt>
              <dd>{stats.scored}</dd>
            </div>
            <div>
              <dt>Conditional</dt>
              <dd>{stats.conditional}</dd>
            </div>
            <div>
              <dt>Cited</dt>
              <dd>{stats.citations}</dd>
            </div>
            <div>
              <dt>Evidence</dt>
              <dd>{stats.evidence}</dd>
            </div>
            <div>
              <dt>Optional</dt>
              <dd>{stats.optional}</dd>
            </div>
            <div>
              <dt>Allow N/A</dt>
              <dd>{stats.allowsNa}</dd>
            </div>
          </dl>

          <div className="prose-drawer-stack">
            <div className="prose-mix" aria-label="Answer types">
              {stats.mix.map(([type, n]) => (
                <Status key={type} tone={SCORED_TYPES.has(type) ? 'ok' : 'neutral'}>
                  {words(type)} {n}
                </Status>
              ))}
            </div>

            <p className="prose-line">
              {stats.scored} of {stats.questions} can carry a score
              {stats.questions - stats.scored > 0 && (
                <Toggletip label="Why the others score nothing">
                  {'The other ' +
                    (stats.questions - stats.scored) +
                    ' collect text, a number, a date or a file, and add nothing to a risk score whatever weight a model gives them.'}
                </Toggletip>
              )}
            </p>

            {effectivePurpose === 'ASSESSMENT' && (
              <p className="prose-line">
                <Status tone="warn">Assessment</Status>
                Checked for scoreability at publish
                <Toggletip label="What the publish check does">
                  A version no bound risk model weighs is refused at publish. Weights are looked up by exact key, so a
                  key that changes shape loses its weight silently.
                </Toggletip>
              </p>
            )}

            {analysis && analysis.warnings.length > 0 && (
              <p className="prose-line">
                <Status tone="warn">{analysis.warnings.length} to read</Status>
                Worth reading before you commit
                <button type="button" className="prose-link" onClick={() => setWarningsOpen(true)}>
                  Read {analysis.warnings.length === 1 ? 'it' : 'them'}
                </button>
              </p>
            )}
          </div>
        </section>
      )}

      {doc && (
        <section className="panel" aria-labelledby="commit-heading">
          <div className="prose-panelhead">
            <h2 id="commit-heading">4. Import it</h2>
          </div>
          <div className="prose-commit">
            <button
              type="button"
              className="primary"
              disabled={!ready || busy || !mayAuthor}
              title={mayAuthor ? undefined : refusalReason('questionnaire.author')}
              onClick={() => void runImport()}
            >
              {busy ? 'Importing' : 'Import as a draft'}
            </button>
            {!mayAuthor && (
              <>
                <Status tone="neutral">Not allowed</Status>
                <Toggletip label="Why you cannot import">{tip(refusalReason('questionnaire.author'))}</Toggletip>
              </>
            )}
            {!ready && !busy && mayAuthor && (
              <span className="prose-line">
                {result !== null ? (
                  <>
                    <Status tone="plain">Imported</Status>
                    Check the document again to write a second draft
                  </>
                ) : stale ? (
                  <>
                    <Status tone="warn">Changed</Status>
                    Check the document again first
                  </>
                ) : drift.state === 'loading' ? (
                  <>
                    <Status tone="neutral">Waiting</Status>
                    Comparing keys with the published version
                  </>
                ) : (
                  <>
                    <Status tone="crit">{blocking.length} blocking</Status>
                    {blocking.length === 1 ? 'One problem above blocks it' : 'Problems above block it'}
                  </>
                )}
              </span>
            )}
            {ready && !busy && (
              <span className="prose-line">
                <Status tone="ok">Ready</Status>
                {stats?.questions ?? 0} {stats?.questions === 1 ? 'question' : 'questions'} as a draft
                {drift.state === 'failed' ? (
                  <Toggletip label="The one rule not checked">
                    Key drift was not checked because the published version did not load, so the API may still refuse
                    it.
                  </Toggletip>
                ) : (
                  <Toggletip label="What was checked">Nothing this screen checks would refuse it.</Toggletip>
                )}
              </span>
            )}
          </div>
        </section>
      )}

      {failure && (
        <div className="error-box" role="alert">
          <h3>The API refused the import</h3>
          <p>{failure.message}</p>
          <p className="muted">{remedy(failure)}</p>
          {failure.issues.length > 0 && <IssueList issues={failure.issues} />}
          <ErrorReference code={failure.code} reference={failure.reference ?? ''} />
        </div>
      )}

      {result && (
        <section className="panel" role="status" aria-labelledby="result-heading">
          <div className="prose-panelhead">
            <Status tone="ok">Imported</Status>
            <h2 id="result-heading">
              Version {result.versionNo}, {result.status.toLowerCase()}
            </h2>
            {result.note !== '' && <Toggletip label="What happens next">{tip(result.note)}</Toggletip>}
            <span className="prose-grow" />
            <a className="button primary" href={'#/questionnaire/' + encodeURIComponent(result.questionnaireId)}>
              Open to publish
            </a>
          </div>
          <dl className="prose-kv">
            <dt>Questionnaire</dt>
            <dd>
              <a className="prose-mono" href={'#/questionnaire/' + encodeURIComponent(result.questionnaireId)}>
                {result.questionnaireId}
              </a>
              {result.questionnaireCreated ? ' (created)' : ' (existing)'}
            </dd>
            <dt>Version</dt>
            <dd className="prose-mono">{result.versionId}</dd>
            <dt>Written</dt>
            <dd>
              {result.questionCount} questions in {result.sections} {result.sections === 1 ? 'section' : 'sections'}
            </dd>
          </dl>
        </section>
      )}

      {listError && (
        <p className="prose-line">
          <Status tone="warn">List unavailable</Status>
          Existing questionnaires cannot be chosen ({listError.code})
          <Toggletip label="What to do">{tip(remedy(listError))}</Toggletip>
        </p>
      )}

      <Dialog shape="drawer" kind="record" open={questionsOpen && doc !== null} onClose={() => setQuestionsOpen(false)} title="Every question in the document">
        {doc && (
          <div className="prose-drawer-stack">
            {doc.sections.map((sec, si) => (
              <div key={sec.key + si}>
                <h3>
                  {sec.title || 'Untitled section'} <span className="prose-mono">{sec.key}</span>
                </h3>
                <ul className="prose-qlist">
                  {sec.questions.map((q, qi) => (
                    <li key={sec.key + si + q.key + qi}>
                      <span className="prose-mono">{q.key || 'no key'}</span>
                      <span className="prose-row-main">
                        <span>{q.prompt || 'no prompt'}</span>
                        <span className="prose-sub">
                          {words(q.answerType)}
                          {q.options.length > 0 && ', ' + q.options.length + ' options'}
                          {!q.isRequired && ', optional'}
                          {q.allowsNa && ', allows N/A'}
                          {q.evidenceRequired && ', evidence required'}
                          {q.visibleWhen && ', shown when ' + q.visibleWhen.questionKey + ' is ' + q.visibleWhen.values.join(' or ')}
                        </span>
                        {q.citations.length > 0 && (
                          <span className="prose-chips">
                            {q.citations.map((c, ci) => (
                              <span key={c + ci} className="prose-cite">
                                {c}
                              </span>
                            ))}
                          </span>
                        )}
                      </span>
                    </li>
                  ))}
                </ul>
              </div>
            ))}
          </div>
        )}
      </Dialog>

      <Dialog shape="drawer" kind="record" open={warningsOpen && (analysis?.warnings.length ?? 0) > 0} onClose={() => setWarningsOpen(false)} title="Worth reading before you commit">
        <ul className="prose-rows">
          {(analysis?.warnings ?? []).map((w, n) => (
            <li key={w + n} className="prose-row">
              {w}
            </li>
          ))}
        </ul>
      </Dialog>
    </div>
  )
}
